import { useQuery } from "@apollo/client"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useSearchParams } from "react-router-dom"
import { useDebounce } from "use-debounce"
import { z } from "zod"
import { ZodURLSearchParamSerializer } from "zod-urlsearchparams"
import { gql } from "~/__generated__"
import { BookmarkPreset, HiddenFilter } from "~/__generated__/graphql"
import { Button } from "~/ui/button"
import { DelayedLoadingSpinner } from "~/ui/delayed-loading-spinner"
import { GraphqlError } from "~/ui/errors"
import FilteredTagList from "~/ui/filtered-tags-list"
import { Form, FormControl, FormField, FormItem } from "~/ui/form"
import GroupsFilter from "~/ui/groups-filter"
import { Input } from "~/ui/input"
import { BookmarkIndexItem } from "./bookmark-index-item"
import { useDocumentTitle } from "~/common/use-document-title"
import { Switch } from "~/components/switch"

export const bookmarkIndexUrlSchema = z.object({
  search: z.string(),
  tagIds: z.array(z.string()),
  hiddenFilter: z.nativeEnum(HiddenFilter),
})

export const bookmarkIndexUrlSerializer = new ZodURLSearchParamSerializer(bookmarkIndexUrlSchema, {
  search: "",
  tagIds: [],
  hiddenFilter: HiddenFilter.HideHidden,
})

export interface Tag {
  id: string
  name: string
}

const query = gql(/* GraphQL */ `
  query Bookmarks($after: String, $filters: BookmarkFiltersInput) {
    bookmarks(first: 50, after: $after, filters: $filters) {
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          id
          ...BookmarkIndexItem
        }
      }
    }

    bookmarkCount: bookmarks(first: 1, filters: { preset: MINE_AND_MY_GROUPS }) {
      totalCount
    }
  }
`)

export const BookmarkIndexScreen = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [groupFilter, setGroupFilter] = useState<string | null>(null)
  useDocumentTitle("Bookmarks")

  const form = useForm({
    resolver: zodResolver(bookmarkIndexUrlSchema),
    defaultValues: bookmarkIndexUrlSerializer.lenientParse(searchParams, {
      search: "",
      tagIds: [],
      hiddenFilter: HiddenFilter.HideHidden,
    }),
  })

  const { watch } = form
  const searchValue = watch("search")
  const [searchValueDebounced] = useDebounce(searchValue, 300)
  const filterTagIds = watch("tagIds")

  useEffect(() => {
    const subscription = watch((values) => {
      // @ts-expect-error anonying type for 'values' from react-hook-form
      setSearchParams(bookmarkIndexUrlSerializer.serialize(values), { replace: true })
    })
    return () => subscription.unsubscribe()
  }, [watch, setSearchParams])

  const { data, previousData, loading, error, fetchMore, refetch } = useQuery(query, {
    variables: {
      after: null,
      filters: {
        search: searchValueDebounced || null,
        preset: BookmarkPreset.MineAndMyGroups,
        tags: filterTagIds,
        groups: groupFilter ? [groupFilter] : undefined,
        hiddenFilter: form.getValues("hiddenFilter"),
      },
    },
  })

  const toggleFilterTag = (tag: Tag) => {
    const currentTagIds = form.getValues("tagIds")
    if (currentTagIds.includes(tag.id)) {
      form.setValue(
        "tagIds",
        currentTagIds.filter((id) => id !== tag.id)
      )
    } else {
      form.setValue("tagIds", [...currentTagIds, tag.id])
    }
  }

  if (error) {
    return <GraphqlError error={error} />
  }

  const bookmarks = (data ?? previousData)?.bookmarks.edges.map((e) => e.node) ?? []
  const bookmarkTotalCount = (data ?? previousData)?.bookmarkCount.totalCount || 0

  return (
    <div>
      <div className="mb-2 flex items-center justify-end">
        <div className="flex items-center space-x-2">
          <Switch
            id="show-hidden"
            checked={form.getValues("hiddenFilter") === HiddenFilter.ShowAll}
            onCheckedChange={(checked) => {
              form.setValue(
                "hiddenFilter",
                checked ? HiddenFilter.ShowAll : HiddenFilter.HideHidden
              )
              refetch()
            }}
          />
          <label htmlFor="show-hidden" className="cursor-pointer text-sm font-medium">
            Show Hidden Bookmarks
          </label>
        </div>
      </div>

      <div className="mx-auto flex max-w-7xl flex-col break-words">
        {/* Search */}
        <div className="mb-4">
          <Form {...form}>
            <form className="space-y-4">
              <FormField
                control={form.control}
                name="search"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="search"
                        placeholder="Search by bookmark name or URL"
                        className="border-0 bg-[#F3F3F4]"
                        {...field}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>

        {/* Filter Tags */}
        <FilteredTagList filterTagIds={filterTagIds} toggleFilterTag={toggleFilterTag} />

        {/* Filter Groups */}
        <GroupsFilter
          groupFilter={groupFilter}
          setGroupFilter={setGroupFilter}
          bookmarksCount={bookmarkTotalCount}
        />

        {/* Bookmarks */}
        {loading ? <DelayedLoadingSpinner /> : null}
        {bookmarks.length ? (
          <ul>
            {bookmarks.map((bookmark) => (
              <BookmarkIndexItem
                key={bookmark.id}
                bookmark={bookmark}
                refetch={refetch}
                filterTagIds={filterTagIds}
                toggleFilterTag={toggleFilterTag}
              />
            ))}
          </ul>
        ) : (
          <p>
            {searchValueDebounced
              ? `No bookmarks found matching "${searchValueDebounced}"`
              : "You don't have any saved bookmarks yet"}
          </p>
        )}

        {/* Load More / Paging */}
        {data?.bookmarks.pageInfo.hasNextPage && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="outline"
              onClick={() => {
                fetchMore({
                  variables: {
                    after: data.bookmarks.pageInfo.endCursor,
                  },
                })
              }}
            >
              Load more
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
